import client from './client';
import { ProjectTasks, UploadType, ProjectTasksComparison, PieceComparisonResponse } from '../interfaces/ExcelUploadInterface';

// Pulls the message the API sent back (e.g. { message: "Account code not found for Cost Code Code: 1124.1" })
// so callers can show the real reason instead of "Request failed with status code 400".
export function extractApiErrorMessage(error: any): string | null {
    const data = error?.response?.data;

    if (typeof data === 'string' && data.trim()) return data.trim();

    if (data && typeof data === 'object') {
        const direct = data.message || data.errorMessage || data.Message || data.detail || data.title;
        if (typeof direct === 'string' && direct.trim()) return direct.trim();

        // ASP.NET model-state style: { errors: { field: ["msg", ...] } }
        if (data.errors && typeof data.errors === 'object') {
            const flattened = Object.values(data.errors as Record<string, unknown>)
                .flatMap((v) => (Array.isArray(v) ? v : [v]))
                .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
            if (flattened.length) return flattened.join(' ');
        }
    }

    return null;
}

/** Result of a WCDP apply-changes POST. `summary` is echoed back by some endpoints. */
export interface WcdpApplyResult {
    message?: string;
    summary?: {
        additions?: number;
        removals?: number;
        updates?: number;
        unchanged?: number;
        intermediatesAdded?: number;
        intermediatesRemoved?: number;
    };
}

// Function to upload an Excel file
export default class ExcelUploadAPI {
    // ── Worksheet-name probes ────────────────────────────────────────────────
    //
    // No popups: the import pages render the failure inline next to the
    // worksheet field, so these throw the API's own message instead.

    static async excelSFSheetNames(file: File): Promise<string[]> {
        try {
            if (!file) {
                throw new Error("File is required.");
            }

            const formData = new FormData();
            formData.append("file", file);

            const response = await client.post('/admin/projectTasks/excelPaintSFSheetNames', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data) return [];

            return response.data;
        } catch (error: any) {
            console.error("Error reading worksheet names:", error);
            throw new Error(extractApiErrorMessage(error) || "Failed to read worksheet names.");
        }
    }

    static async excelMFSheetNames(file: File): Promise<string[]> {
        try {
            if (!file) {
                throw new Error("File is required.");
            }

            const formData = new FormData();
            formData.append("file", file);

            const response = await client.post('/admin/projectTasks/excelPaintMFSheetNames', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data) return [];

            return response.data;
        } catch (error: any) {
            console.error("Error reading worksheet names:", error);
            throw new Error(extractApiErrorMessage(error) || "Failed to read worksheet names.");
        }
    }

    // ── WCDP apply ───────────────────────────────────────────────────────────

    /**
     * Writes a previewed WCDP import. `uploadType` IS the endpoint URL (see the
     * UploadType enum). Always re-post the file/projectID/sheetName that were
     * compared — never live form state.
     */
    static async uploadWcdpApplyChanges(
        file: File,
        projectID: string,
        uploadType: UploadType,
        sheetName?: string,
    ): Promise<WcdpApplyResult | null> {
        try {
            if (!file || !projectID) {
                throw new Error("File and projectID are required.");
            }

            const formData = new FormData();
            formData.append("file", file);
            formData.append("projectID", projectID);
            if (sheetName) formData.append("sheetName", sheetName);

            const response = await client.post(uploadType, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data) return null;

            return response.data as WcdpApplyResult;
        } catch (error: any) {
            console.error("Error applying import:", error);
            // The page surfaces the message in a toast, so no popup here.
            throw new Error(extractApiErrorMessage(error) || "Failed to apply changes.");
        }
    }

    // ── WCDP comparisons (preview only — these never write) ──────────────────

    static async uploadPaintSFExcelComparison(file: File, projectID: string, sheetName: string): Promise<ProjectTasksComparison | null> {
        try {
            if (!file || !projectID || !sheetName) {
                throw new Error("File, projectID, and sheetName are required.");
            }

            const formData = new FormData();
            formData.append("file", file);
            formData.append("projectID", projectID);
            formData.append("sheetName", sheetName);

            const response = await client.post('/admin/projectTasks/uploadPaintSFExcelComparison', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data) return null;

            return response.data as ProjectTasksComparison;
        } catch (error: any) {
            console.error("Error comparing file:", error);
            throw new Error(extractApiErrorMessage(error) || "Failed to compare file.");
        }
    }

    /** UPF phase-budget PDF → piece comparison (same shape as Drywall SF). Never writes. */
    static async uploadUpfPiecePdfComparison(file: File, projectID: string): Promise<ProjectTasksComparison | null> {
        try {
            if (!file || !projectID) {
                throw new Error("File and projectID are required.");
            }

            const formData = new FormData();
            formData.append("file", file);
            formData.append("projectID", projectID);

            const response = await client.post('/admin/upf/projectTasks/uploadPiecePdfComparison', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data) return null;

            return response.data as ProjectTasksComparison;
        } catch (error: any) {
            console.error("Error comparing file:", error);
            throw new Error(extractApiErrorMessage(error) || "Failed to compare file.");
        }
    }

    static async uploadDrywallSFExcelComparison(file: File, projectID: string): Promise<ProjectTasksComparison | null> {
        try {
            if (!file || !projectID) {
                throw new Error("File and projectID are required.");
            }

            const formData = new FormData();
            formData.append("file", file);
            formData.append("projectID", projectID);

            const response = await client.post('/admin/projectTasks/uploadDrywallSFExcelComparison', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data) return null;

            return response.data as ProjectTasksComparison;
        } catch (error: any) {
            console.error("Error comparing file:", error);
            throw new Error(extractApiErrorMessage(error) || "Failed to compare file.");
        }
    }

    static async uploadDrywallMFExcelComparison(file: File, projectID: string): Promise<ProjectTasksComparison | null> {
        try {
            if (!file || !projectID) {
                throw new Error("File and projectID are required.");
            }

            const formData = new FormData();
            formData.append("file", file);
            formData.append("projectID", projectID);

            const response = await client.post('/admin/projectTasks/uploadDrywallMFExcelComparison', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data) return null;

            return response.data as ProjectTasksComparison;
        } catch (error: any) {
            console.error("Error comparing file:", error);
            throw new Error(extractApiErrorMessage(error) || "Failed to compare file.");
        }
    }

    static async uploadPaintMFExcelComparison(file: File, projectID: string, sheetName: string): Promise<ProjectTasksComparison | null> {
        try {
            if (!file || !projectID || !sheetName) {
                throw new Error("File, projectID, and sheetName are required.");
            }

            const formData = new FormData();
            formData.append("file", file);
            formData.append("projectID", projectID);
            formData.append("sheetName", sheetName);

            const response = await client.post('/admin/projectTasks/uploadPaintMFExcelComparison', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data) return null;

            return response.data as ProjectTasksComparison;
        } catch (error: any) {
            console.error("Error comparing file:", error);
            throw new Error(extractApiErrorMessage(error) || "Failed to compare file.");
        }
    }

    static async uploadPaintMFExcelComparisonV1(file: File, projectID: string, sheetName: string): Promise<ProjectTasksComparison | null> {
        try {
            if (!file || !projectID || !sheetName) {
                throw new Error("File, projectID, and sheetName are required.");
            }

            const formData = new FormData();
            formData.append("file", file);
            formData.append("projectID", projectID);
            formData.append("sheetName", sheetName);

            const response = await client.post('/admin/projectTasks/uploadPaintMFExcelComparisonV1', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data) return null;

            return response.data as ProjectTasksComparison;
        } catch (error: any) {
            console.error("Error comparing file:", error);
            throw new Error(extractApiErrorMessage(error) || "Failed to compare file.");
        }
    }

    // ── 2020ph piece CSV ─────────────────────────────────────────────────────

    static async uploadPieceCsvComparison(file: File): Promise<PieceComparisonResponse | null> {
        try {
            if (!file) {
                throw new Error("File is required.");
            }

            const formData = new FormData();
            formData.append("file", file);

            const response = await client.post('/admin/2020ph/projectTasks/uploadPieceCsvComparison', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data) {
                console.error("Upload failed:", response.data?.errorMessage || "Unknown error");
                return null;
            }

            const data: PieceComparisonResponse = response.data;
            return data;
        } catch (error: any) {
            console.error("Error uploading file:", error);
            // The page renders the message inline, so no popup here.
            throw new Error(extractApiErrorMessage(error) || "Failed to upload file.");
        }
    }

    static async uploadPieceCsvApplyChanges(file: File): Promise<ProjectTasks | null> {
        try {
            if (!file) {
                throw new Error("File is required.");
            }

            const formData = new FormData();
            formData.append("file", file);

            const response = await client.post('/admin/2020ph/projectTasks/uploadPieceCsvApplyChanges', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data) {
                console.error("Upload failed:", response.data?.errorMessage || "Unknown error");
                return null;
            }

            return response.data;
        } catch (error: any) {
            console.error("Error uploading file:", error);
            // The page surfaces the message in a toast, so no popup here.
            throw new Error(extractApiErrorMessage(error) || "Failed to apply changes.");
        }
    }
}
